import { bind } from '@react-rxjs/core';
import { createSignal } from '@react-rxjs/utils';
import {
  catchError,
  concatMap,
  from,
  iif,
  map,
  mergeMap,
  of,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { addMalfunctionToCurrentQIfExists } from '../../services/inspection';
import { refreshMalfunctions } from '../../services/malfunctions';
import { request } from '../../services/request';
import { Progress } from '../../types';
import { MalfunctionItem } from '../../types/malfunction';
import { resizeFile } from '../../utils/resizeFile';
import { Values } from './types';

export const MAX_IMAGE_SIZE = 2000;

interface Metadata {
  url: string;
  contentType: 'image/png' | 'image/jpeg';
  name: string;
}

type MalfunctionPhotoUploadState = [File, Metadata];

interface MalfunctionUploadState extends Progress {
  files: MalfunctionPhotoUploadState[];
  payload?: Partial<MalfunctionItem>;
  error?: string;
  running?: boolean;
  result?: MalfunctionItem;
}

export const [setCreateMalfunctionDialog$, setCreateMalfunctionDialog] =
  createSignal<boolean>();

export const [useCreateMalfunctionDialog, dialog$] = bind(
  setCreateMalfunctionDialog$,
  false
);

export const [submitSignal$, submitMalfunction] = createSignal<Values>();

const uploadFile = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return request<Metadata>(`/gateway/malfunction-service/v20231213/photos/`, {
    method: 'POST',
    body: formData,
  });
};

export const [useSubmitState, submit$] = bind(
  submitSignal$.pipe(
    switchMap((values) =>
      iif(
        () => values.files.length < 1,
        of<MalfunctionUploadState>({
          total: 0,
          progress: 0,
          files: [],
          running: true,
        }),
        from(values.files).pipe(
          concatMap((file) => from(resizeFile(file!, MAX_IMAGE_SIZE))),
          mergeMap((file) =>
            uploadFile(file).pipe(
              map<Metadata, MalfunctionPhotoUploadState>((metadata) => [
                file,
                metadata,
              ])
            )
          ),
          /** update file upload progress */
          scan<MalfunctionPhotoUploadState, MalfunctionUploadState>(
            (acc, value) => {
              const newAcc = { ...acc };
              newAcc.progress = newAcc.progress! + 1;
              newAcc.files!.push(value);
              return newAcc;
            },
            {
              total: values.files.length,
              progress: 0,
              files: [],
              running: true,
            }
          ),
          startWith<MalfunctionUploadState>({
            total: values.files.length,
            progress: 0,
            files: [],
            running: true,
          }),
          catchError(() => {
            return of<MalfunctionUploadState>({
              error: 'UPLOAD_ERROR',
              running: false,
              files: [],
              total: 0,
            });
          })
        )
      ).pipe(
        switchMap((state) =>
          iif(
            () => !!state.error || (state.progress ?? 0) < state.total,
            of(state),
            of(state).pipe(
              /** create malfunction payload while mapping uploaded images to form data */
              map<MalfunctionUploadState, MalfunctionUploadState>((state) => {
                return {
                  ...state,
                  payload: {
                    objectId: values.vehicle.value,
                    name: values.name!,
                    description: values.description,
                    malfunctionType: values.type.value,
                    photos: state.files.map(([, metadata]) => metadata.name),
                  },
                };
              }),
              switchMap((state) =>
                request<MalfunctionItem>(
                  '/gateway/malfunction-service/v20231215/malfunctions/',
                  {
                    method: 'POST',
                    body: JSON.stringify(state.payload),
                    headers: { 'Content-Type': 'application/json' },
                  }
                ).pipe(
                  map((result) => ({
                    ...state,
                    running: false,
                    error: undefined,
                    result,
                  })),
                  catchError(() => {
                    return of({
                      ...state,
                      error: 'SAVE_ERROR',
                      running: false,
                    });
                  })
                )
              ),
              startWith(state)
            )
          )
        ),
        tap((state) => {
          if (!state.error && !state.running) {
            refreshMalfunctions();
            setCreateMalfunctionDialog(false);
            if (state.result) {
              addMalfunctionToCurrentQIfExists({
                id: state.result.id,
                name: state.result.name,
              });
            }
          }
        })
      )
    )
  ),
  null
);
